<script setup>
import { ref } from 'vue'
import _ from 'lodash'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { getSigninUrl } from '@/Utils/authRedirect'

import MainSidebarItem from './MainSidebarItem.vue';
import IconCamera from '../icon/IconCamera.vue';
import IconCameraCinema from '../icon/IconCameraCinema.vue';
import IconTV from '../icon/IconTV.vue';
import IconLogout from '../icon/IconLogout.vue';
import IconLightning from '../icon/IconLightning.vue';
import IconTranscoder from '../icon/IconTranscoder.vue';
import IconMediaflow from '../icon/IconMediaflow.vue';

import IconHighlighter from '../icon/IconHighlighter.vue';
import IconClosedCaption from '../icon/IconClosedCaption.vue';
import IconHosting from '../icon/IconHosting.vue';
import IconPlayer from '../icon/IconPlayer.vue';
import IconPlaylist from '../icon/IconPlaylist.vue';
import IconShowcase from '../icon/IconShowcase.vue';
import IconBanknote from '../icon/IconBanknote.vue';
import IconGlobe from '../icon/IconGlobe.vue';
import IconOttApp from '../../components/icon/IconOttApp.vue';
import IconGear from '../icon/IconGear.vue';
import IconTeam from '../icon/IconTeam.vue';
import IconCreditCard from '../icon/IconCreditCard.vue';
import IconCalendarPlus from '../icon/IconCalendarPlus.vue';
import IconUpgrade from '../icon/IconUpgrade.vue';
import IconBook from '../icon/IconBook.vue';
import IconHelp from '../icon/IconHelp.vue';
import IconDownload from '../icon/IconDownload.vue';
import IconMultiview from '../icon/IconMultiview.vue';
import IconStars from '../icon/IconStars.vue';
import IconBase from '../icon/IconBase.vue';
import IconChart from '../icon/IconChart.vue'
import IconList from '../icon/IconList.vue'
import { openCalendly } from '@/utils'

const props = defineProps({
  expanded: {
    type: Boolean,
  },
})

const { notify } = useNotification()
const router = useRouter()
const store = useStore()

const forceLogout = async (ev) => {
  ev.preventDefault();
  ev.stopPropagation();
  await store.dispatch('Auth/logout');
  process.env.VUE_APP_API_ENV === 'prod' ? (window.location.href = getSigninUrl()) : router.push('/login');
  notify({ group: 'info', text: 'Logged out' });
  window.trackEvent('Logged Out');
}

</script>

<template>
  <nav aria-label="Main navigation"
    class="sidebar-nav flex flex-col flex-1 min-h-0 overflow-y-auto overscroll-y-contain overflow-x-hidden h-full touch-pan-y pb-4">
    <!-- Stream: everyday broadcasting + its multistream destinations -->
    <p v-if="expanded" class="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-surface-6 select-none">
      Stream
    </p>
    <ul class="space-y-0.5" :class="{ 'mx-auto w-fit': !expanded }">
      <li>
        <MainSidebarItem :expanded="expanded" :icon="IconCamera" title="Livestream" to="/dashboard" />
      </li>
      <li>
        <MainSidebarItem :expanded="expanded" :icon="IconCameraCinema" title="Pre-Recorded Streams" to="/scheduler" />
      </li>
      <li>
        <MainSidebarItem :expanded="expanded" :icon="IconTV" title="TV Playout" to="/tv-playout/v2" />
      </li>
      <li>
        <MainSidebarItem :expanded="expanded" :icon="IconTranscoder" title="Transcoder" to="/transcoder" />
      </li>
    </ul>

    <!-- Content & Monetization: host, publish, monetize and measure -->
    <div class="mt-5">
      <p v-if="expanded" class="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-surface-6 select-none">
        Content &amp; Monetization
      </p>
      <div v-else class="mx-auto my-2 h-px w-6 bg-surface-3" aria-hidden="true"></div>
      <ul class="space-y-0.5" :class="{ 'mx-auto w-fit': !expanded }">
        <li>
          <MainSidebarItem :expanded="expanded" :icon="IconHosting" title="Content Library" to="/vods" />
        </li>
        <li>
          <MainSidebarItem :expanded="expanded" :icon="IconBanknote" title="Paywall" to="/paywall/v2" />
        </li>
        <li>
          <MainSidebarItem :expanded="expanded" :icon="IconGlobe" title="Sites" to="/sites" />
        </li>
        <li>
          <MainSidebarItem :expanded="expanded" :icon="IconPlayer" title="Dynamic Player" to="/dynamic" />
        </li>
        <li>
          <MainSidebarItem :expanded="expanded" :icon="IconPlaylist" title="Playlists" to="/playlists" />
        </li>
        <li>
          <MainSidebarItem :expanded="expanded" :icon="IconChart" title="Analytics" to="/analytics" />
        </li>
        <li>
          <MainSidebarItem :expanded="expanded" :icon="IconOttApp" title="OTT Apps" to="/ott-apps" />
        </li>
      </ul>
    </div>

    <!-- Advanced: lower-usage production tools & specialized modes -->
    <div class="mt-5">
      <p v-if="expanded" class="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-surface-6 select-none">
        Advanced
      </p>
      <div v-else class="mx-auto my-2 h-px w-6 bg-surface-3" aria-hidden="true"></div>
      <ul class="space-y-0.5" :class="{ 'mx-auto w-fit': !expanded }">
        <li>
          <MainSidebarItem :expanded="expanded" :icon="IconClosedCaption" title="Captioning" to="/captioning"
            :badge-title="expanded ? 'Beta' : null" />
        </li>
        <li>
          <MainSidebarItem :expanded="expanded" :icon="IconMediaflow" title="Cloud Production" to="/cloud-production" />
        </li>
        <li>
          <MainSidebarItem :expanded="expanded" :icon="IconMultiview" title="Multiview" to="/multiview" />
        </li>
        <li>
          <MainSidebarItem :expanded="expanded" :icon="IconHighlighter" title="Live Clipping" to="/highlight" />
        </li>
        <li>
          <MainSidebarItem :expanded="expanded" :icon="IconLightning" title="Sub-Second Streaming" to="/sub-second" />
        </li>
      </ul>
    </div>

    <div class="mt-6">
      <p v-if="expanded" class="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-surface-6 select-none">
        Account
      </p>
      <ul class="space-y-0.5" :class="{ 'mx-auto w-fit': !expanded }">
        <li>
          <MainSidebarItem :expanded="expanded" :icon="IconGear" title="Settings" to="/manage/account" />
        </li>
        <li>
          <MainSidebarItem :expanded="expanded" :icon="IconList" title="Activity log" to="/activity-log" />
        </li>
        <li>
          <MainSidebarItem :expanded="expanded" :icon="IconTeam" title="Team" to="/manage/team" />
        </li>
        <li>
          <MainSidebarItem :expanded="expanded" :icon="IconCreditCard" title="Billing" to="/manage/billing" />
        </li>
        <li>
          <MainSidebarItem :expanded="expanded" :icon="IconUpgrade" title="Upgrade" to="/subscribe" />
        </li>
      </ul>
    </div>

    <div class="mt-5">
      <p v-if="expanded" class="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-surface-6 select-none">
        Support
      </p>
      <div v-else class="mx-auto my-2 h-px w-6 bg-surface-3" aria-hidden="true"></div>
      <ul class="space-y-0.5" :class="{ 'mx-auto w-fit': !expanded }">
        <li>
          <MainSidebarItem :class="{ 'w-full': expanded }" :expanded="expanded" :icon="IconHelp" title="Help" onclick="Intercom('show')" />
        </li>
        <li>
          <MainSidebarItem :expanded="expanded" :icon="IconBook" title="Docs" to="https://docs.castr.com/" />
        </li>
        <li>
          <MainSidebarItem :class="{ 'w-full': expanded }" :expanded="expanded" :icon="IconCalendarPlus" title="Book a Meeting"
            @click="openCalendly()" />
        </li>
      </ul>
    </div>

    <div class="mt-5">
      <div v-if="!expanded" class="mx-auto my-2 h-px w-6 bg-surface-3" aria-hidden="true"></div>
      <ul class="space-y-0.5" :class="{ 'mx-auto w-fit': !expanded }">
        <li>
          <MainSidebarItem :class="{ 'w-full': expanded }" :expanded="expanded" :icon="IconLogout" title="Logout" @click="forceLogout" />
        </li>
      </ul>
    </div>

    <div class="mt-auto pt-4">
      <a v-if="expanded" target="_blank" href="https://desktop.castr.com/"
        class="group flex items-center gap-2.5 rounded-lg px-3 h-9 bg-surface-1 ring-1 ring-white/5 text-surface-8 hover:text-white hover:bg-surface-2 hover:ring-white/10 transition-colors hover:no-underline">
        <icon-base fill="none" class="shrink-0 text-surface-7 group-hover:text-main-light transition-colors">
          <component :is="IconDownload" />
        </icon-base>
        <span class="flex-1 min-w-0 text-tiny font-medium truncate">Get Desktop App</span>
      </a>
      <a v-else target="_blank" href="https://desktop.castr.com/" title="Get Desktop App"
        class="mx-auto grid place-items-center size-9 rounded-lg bg-surface-1 ring-1 ring-white/5 text-surface-7 hover:text-main-light hover:bg-surface-2 transition-colors hover:no-underline">
        <icon-base fill="none">
          <component :is="IconDownload" />
        </icon-base>
      </a>
    </div>
  </nav>
</template>

<style scoped>
.sidebar-nav {
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
  /* Firefox: thin, invisible until hover */
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

.sidebar-nav:hover {
  scrollbar-color: var(--surface-4) transparent;
}

/* WebKit: subtle overlay-style scrollbar, hidden until hover */
.sidebar-nav::-webkit-scrollbar {
  width: 6px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 9999px;
}

.sidebar-nav:hover::-webkit-scrollbar-thumb {
  background-color: var(--surface-4);
}

.sidebar-nav:hover::-webkit-scrollbar-thumb:hover {
  background-color: var(--surface-5);
}
</style>
